import { type Editor } from '@tiptap/core'
import { proxy, useSnapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import type { RectBounds } from '@/utils/events'

import type { Column } from '~shared/rundown/column'
import type { Cue, CueId } from '~shared/rundown/cue'

import {
  CellKey,
  makeCellKey,
  parseCellKey,
} from '@/features/rundown/cell/cell.schema'
import { type CellUploadRegistry } from '@/features/rundown/cell/upload/upload'
import { modalActions } from '@/features/rundown/modals/modal.store'

import * as logger from '../../../utils/logger'
import { CellDomUtils } from '../cell/cell.schema'

import {
  SaveEntry,
  SaveKey,
  SavePayload,
  gridSaveActions,
  saveKeys,
} from './grid.save'

type GridEditorClickCoords = {
  x: number
  y: number
  selectionRect: RectBounds | null
} | null

type GridEditorPasteData = {
  html: string
  text: string
}

type ContextMenuState =
  | {
      type: 'cell'
      cellKey: CellKey
      isReadOnly: boolean
      x: number
      y: number
    }
  | {
      type: 'cue'
      cueId: Cue['id']
      x: number
      y: number
    }
  | {
      type: 'column'
      columnId: Column['id']
      x: number
      y: number
    }

export type ContextMenuType = ContextMenuState['type']

export type GridSelection =
  | {
      type: 'cell'
      cellKey: CellKey
      cueId: Cue['id']
      columnId: Column['id']
    }
  | {
      type: 'cue'
      cueId: Cue['id']
    }
  | null

type GridState = {
  // Selection
  selection: GridSelection

  // Cells
  editingCell: CellKey | null

  // Editor
  editor: {
    /** Did user double click in a specific content location? */
    clickCoords: GridEditorClickCoords | null
    pendingPaste: GridEditorPasteData | null
    /** Has user made any changes to the cell? */
    isDirty: boolean
    /** Was the cell updated somewhere else while user was editing? */
    isConflicting: boolean
    getHTMLref: Editor['getHTML'] | null
  }

  /** Registry tracking file uploads and progress for cells */
  cellUploadRegistry: CellUploadRegistry

  /** Registry tracking save progress for cells */
  saveRegistry: Record<SaveKey, SaveEntry>

  contextMenu: ContextMenuState | null
}

const initialEditorState: GridState['editor'] = {
  clickCoords: null,
  pendingPaste: null,
  isDirty: false,
  isConflicting: false,
  getHTMLref: null,
}

export const gridState: GridState = proxy({
  // Selection
  selection: null,

  // Cells
  editingCell: null,

  // Active cell editor
  editor: deepClone(initialEditorState),

  cellUploadRegistry: {},

  saveRegistry: {},

  contextMenu: null,
})

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(gridState, { name: 'gridState', enabled: true })
}

// #endregion

type FinishEditingTrigger = 'explicit' | 'blur' | 'force'

export const gridStateActions = {
  setEditingCell: (cellKey: CellKey): boolean => {
    // Already editing this cell, do nothing
    if (gridState.editingCell === cellKey) return true

    // Close out the previous session first (save-preserving).
    // 'blur' because focus is moving to the new cell, not back to the old one.
    if (gridState.editingCell !== null) {
      gridStateActions.editor.finishEditing('blur')

      // finishEditing can refuse to end the session (conflict dialog is open,
      // waiting on user decision). Don't steal the editor out from under it.
      if (gridState.editingCell !== null) {
        logger.info(
          '[Editor action]: setEditingCell blocked by unresolved session',
          gridState.editingCell,
        )
        return false
      }
    }

    gridState.editingCell = cellKey

    return true
  },

  setEditingCellAndClickEvent: (
    cellKey: CellKey,
    clickCoords: GridEditorClickCoords,
  ) => {
    if (!gridStateActions.setEditingCell(cellKey)) return

    gridState.editor.clickCoords = clickCoords
  },

  setEditingCellAndPaste: (
    cellKey: CellKey,
    pasteData: GridEditorPasteData,
  ) => {
    if (!gridStateActions.setEditingCell(cellKey)) return

    gridState.editor.pendingPaste = pasteData
  },

  resetEditingCell: () => {
    gridState.editingCell = null
    gridStateActions.editor.resetEditor()
  },

  /**
   * Namespace for editor related actions
   */
  editor: {
    /**
     * Tries to get latest HTML content from active Tiptap editor.
     * Returns null reference doesn't work.
     */
    getEditorContent: (): string | null => {
      return gridState.editor.getHTMLref?.() || null
    },

    resetEditor: () => {
      gridState.editor = deepClone(initialEditorState)
    },

    /**
     * Check whether cell needs to be saved.
     * Takes into account dirty state and failed save state.
     */
    needsSave: (): boolean => {
      const cellKey = gridState.editingCell
      if (!cellKey) return false

      const entry = gridState.saveRegistry[saveKeys.cell(cellKey)]

      return gridState.editor.isDirty || entry?.status === 'error' // Only count `error`, not `pending` in this check
    },

    /**
     * Ends the editing session.
     * Persists via the save registry when content is unpersisted,
     * otherwise discards. The single exit path for every editor session.
     *
     * Distinguishes between trigger types for different usecases:
     *
     * - `explicit`: default, does all checks, used by save command
     * - `blur`: skip focusing cell after ending, used by clickoutside command
     * - `force`: skip conflict check, used by conflict "Overwrite" command
     *
     * @remarks
     *   Only to be used by the Tiptap editor. The `SelectColumnCell`'s editor
     *   has different internals and its own handler.
     */
    finishEditing: (trigger: FinishEditingTrigger = 'explicit') => {
      logger.info('[Editor action]: finishEditing', trigger)

      if (!gridStateActions.editor.needsSave()) {
        gridStateActions.editor.discard(trigger)
        return // Prevent save
      }

      if (gridState.editor.isConflicting && trigger !== 'force') {
        const cellKey = gridState.editingCell
        if (!cellKey) throw new Error('Conflict dialog requires cellKey')
        void modalActions.open('EDITOR_CONFLICT_DIALOG', { cellKey })
        return // Prevent save
      }

      gridStateActions.editor._commitSave(trigger)
    },

    /**
     * Discards any pending changes and exits editing mode.
     */
    discard: (trigger: FinishEditingTrigger = 'explicit') => {
      logger.info('[Editor action]: discard')

      if (trigger !== 'blur') {
        const cellKey = gridState.editingCell
        if (!cellKey) return
        const cellElm = CellDomUtils.queryValue('data-cell-key', cellKey)
        gridSelectionActions.focus(cellElm)
      }

      gridStateActions.resetEditingCell()
    },

    /**
     * Internal: performs the actual save operation.
     *
     * @private
     */
    _commitSave: (trigger: FinishEditingTrigger = 'explicit') => {
      logger.info('[Editor action]: _commitSave')

      const cellKey = gridState.editingCell
      if (!cellKey) return

      const contentAsHTML = gridStateActions.editor.getEditorContent()

      // Guard: Prevent saving if editor instance is unavailable
      if (contentAsHTML === null) {
        logger.error('[Editor action]: _commitSave with no editor ref', cellKey)
        gridStateActions.editor.discard(trigger)
        return // abort save
      }

      // Release editor immediately
      gridStateActions.resetEditingCell()

      const contentPayload = {
        kind: 'cellText',
        cellKey: cellKey,
        content: {
          text: contentAsHTML,
        },
      } satisfies SavePayload

      gridSaveActions.commit(saveKeys.cell(cellKey), contentPayload)

      if (trigger !== 'blur') {
        const { cueId, columnId } = parseCellKey(cellKey)

        // Fire after potential re-renders
        requestAnimationFrame(() => {
          gridSelectionActions.selectCell(cueId, columnId, { focus: true })
        })
      }
    },
  },

  /**
   * Namespace for menu related actions
   */
  contextMenu: {
    open: (payload: ContextMenuState) => {
      gridState.contextMenu = payload
    },
    close: () => {
      gridState.contextMenu = null
    },
  },
}

export function useGridSnap() {
  return useSnapshot(gridState)
}

type GridSelectionOptions = {
  focus?: boolean
  scrollIntoView?: boolean
}

export const gridSelectionActions = {
  select: (selection: GridSelection, options: GridSelectionOptions = {}) => {
    gridState.selection = selection

    if (!selection || !options.focus) return

    if (selection.type === 'cell') {
      const cellElm = CellDomUtils.queryValue(
        'data-cell-key',
        selection.cellKey,
      )

      gridSelectionActions.focus(cellElm, options)
    } else {
      // gridSelectionActions.focusCue(selection.cueId)
    }
  },

  selectCell: (
    cueId: CueId,
    columnId: Column['id'],
    options: GridSelectionOptions = {},
  ) => {
    const cellKey = makeCellKey(cueId, columnId)

    gridSelectionActions.select(
      {
        type: 'cell',
        cueId,
        columnId,
        cellKey: cellKey,
      },
      options,
    )
  },

  selectCue: (cueId: CueId, options: GridSelectionOptions = {}) => {
    gridSelectionActions.select(
      {
        type: 'cue',
        cueId,
      },
      options,
    )
  },

  clear: () => {
    gridState.selection = null
  },

  /**
   * Moves focus to a grid element.
   *
   * @returns {boolean} Whether element was present and focusable.
   */
  focus: (
    element: HTMLElement | null,
    options?: Pick<GridSelectionOptions, 'scrollIntoView'>,
  ): boolean => {
    if (!element) return false

    if (options?.scrollIntoView) {
      element.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'auto',
      })
    }

    element.focus({ preventScroll: true })

    return true
  },
}
